import { HttpClient, HttpEvent, HttpHeaders, HttpParams, HttpRequest } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { CookieService } from 'ngx-cookie-service';
import { Observable } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import {
  EditableTextList,
  ReportTemplate,
  ReportTemplateImageList,
  ReportTemplateListItem,
  ReportTemplateNew,
  ReportTemplateUpdate,
  ResponseIdList,
  UpdateIdList,
  UpdateResultList
} from '../../_offline/models/report-preview/report-template';
import { HttpService } from '../http.service';
import { ReportTemplateContractService } from '../interfaces/report-template-contract.service';

@Injectable({
  providedIn: 'root'
})
export class ReportTemplateOnlineService extends HttpService implements ReportTemplateContractService {

  constructor(
    private http: HttpClient,
    private cookieService: CookieService,
    private router: Router) {
    super();
    super.setCookieService(cookieService);
    super.setHttpClient(http);
    super.setRouter(router);
  }

  // TP01 GET /api/v1/templates
  getReportTemplates(subjectModelId?: string, enabledOnly?: boolean): Observable<ReportTemplateListItem[]> {
    const endpoint = super.getEndpoint('templates');

    let httpParams = new HttpParams();
    if (subjectModelId) {
      httpParams = httpParams.set('md', subjectModelId);
    }
    if (enabledOnly !== null && enabledOnly !== undefined) {
      httpParams = httpParams.set('enabledOnly', String(enabledOnly));
    }

    return this.http.get<ReportTemplateListItem[]>(endpoint,
      {  withCredentials: super.getWithCredentialsOption(), params: httpParams })
      .pipe(
        map(
          response => response as ReportTemplateListItem[]));
  }

  // TP02 GET /api/v1/templates/{templateId}
  getReportTemplate(reportTemplateId: string): Observable<ReportTemplate> {
    const endpoint = super.getEndpoint('templates', reportTemplateId);

    return this.http.get<ReportTemplate>(endpoint,
      {  withCredentials: super.getWithCredentialsOption() })
      .pipe(map(response => response as ReportTemplate));
  }

  // TP07 GET /api/v1/templates/{templateId}/editableTexts
  getEditableTexts(reportTemplateId: string, reportId: string): Observable<EditableTextList[]> {
    const endpoint = super.getEndpoint('templates', reportTemplateId, 'editableTexts');

    let httpParams = new HttpParams();
    if (reportId) {
      httpParams = httpParams.set('reportId', reportId);
    }

    return this.http.get<EditableTextList[]>(endpoint,
      {  withCredentials: super.getWithCredentialsOption(), params: httpParams })
      .pipe(map(response => response as EditableTextList[]));
  }

  // TP04 PUT /api/v1/templates/{templateId}
  updateReportTemplate(reportTemplateUpdate: ReportTemplateUpdate): Observable<ReportTemplate> {

    const endpoint = super.getEndpoint('templates', reportTemplateUpdate.id);

    return this.http.put<ReportTemplate>(
      endpoint,
      reportTemplateUpdate,
      {  withCredentials: super.getWithCredentialsOption() }
    );
  }

  // TP03 POST /api/v1/templates
  addReportTemplate(reportTemplateNew: ReportTemplateNew): Observable<ReportTemplate> {

    const endpoint = super.getEndpoint('templates');

    return this.http.post<ReportTemplate>(
      endpoint,
      reportTemplateNew,
      {  withCredentials: super.getWithCredentialsOption() }
    );
  }

  // TP05 GET /api/v1/templates/{templateId}/export
  exportReportTemplate(reportTemplateId: string): Observable<Blob> {
    const endpoint = super.getEndpoint('templates', reportTemplateId, 'export');

    return this.http.get(endpoint, {
       withCredentials: super.getWithCredentialsOption(), responseType: 'blob'
    });
  }

  // TP06 PUT /api/v1/templates/import
  importReportTemplate(file: File, reportTemplate?: ReportTemplateListItem): Observable<HttpEvent<{}>> {
    const formData = new FormData();
    formData.append('file', file, file.name);

    const endpoint = super.getEndpoint('templates', 'import');

    let httpParams = new HttpParams();
    if (reportTemplate) {
      httpParams = httpParams.set('templateId', reportTemplate.id);
    }

    const req = new HttpRequest('PUT', endpoint, formData,
      {  withCredentials: super.getWithCredentialsOption(), params: httpParams });
    return this.http.request(req);
  }

  // TP08 GET /api/v1/templates/models
  getModelsForTemplates(reportTemplateId?: string, subjectModelId?: string): Observable<ResponseIdList[]> {
    const endpoint = super.getEndpoint('templates', 'models');

    let httpParams = new HttpParams();
    if (reportTemplateId) {
      httpParams = httpParams.set('rt', reportTemplateId);
    }
    if (subjectModelId) {
      httpParams = httpParams.set('md', subjectModelId);
    }

    return this.http.get<ResponseIdList[]>(endpoint,
      {  withCredentials: super.getWithCredentialsOption(), params: httpParams })
      .pipe(map(response => response as ResponseIdList[]));
  }

  // TP09 PUT /api/v1/templates/{templateId}/models
  updateModelsForTemplates(templateId: string, updateModelIdList: UpdateIdList): Observable<UpdateResultList> {

    const endpoint = super.getEndpoint('templates', templateId, 'models');

    return this.http.put<UpdateResultList>(
      endpoint,
      updateModelIdList,
      {  withCredentials: super.getWithCredentialsOption() }
    );
  }

  // TP10 GET /api/v1/templates/{templateId}/images
  getReportTemplateImages(templateId: string): Observable<ReportTemplateImageList[]> {
    const endpoint = super.getEndpoint('templates', templateId, 'images');

    return this.http.get<ReportTemplateImageList[]>(endpoint,
      {  withCredentials: super.getWithCredentialsOption() });
  }

  // TP11 POST /api/v1/templates/{templateId}/images
  updateReportTemplateImage(templateId: string, file: File): Observable<HttpEvent<{}>> {
    const formData = new FormData();
    formData.append('file', file, file.name);

    const endpoint = super.getEndpoint('templates', templateId, 'images');

    const req = new HttpRequest('POST', endpoint, formData,
      {  withCredentials: super.getWithCredentialsOption() });
    return this.http.request(req);
  }

  // TP12 DELETE /api/v1/templates/{templateId}/images
  deleteReportTemplateImage(templateId: string, fileName: string): Observable<any> {
    const endpoint = super.getEndpoint('templates', templateId, 'images');

    let headers: HttpHeaders = new HttpHeaders();
    headers = headers.append('Content-Type', 'application/json');
    headers = headers.append('responseType', 'json');
    return this.http.request('DELETE', endpoint, {
      'body': '"' + fileName + '"', 'responseType': 'json', headers,
      withCredentials: super.getWithCredentialsOption()
    });
  }

  /** 【TP13】 レポートテンプレートを削除
   * DELETE /api/v1/templates/{templateId}
   * @param  {string} templateId
   * @returns Observable
   */
  deleteReportTemplate(templateId: string): Observable<any> {
    const endpoint = super.getEndpoint('templates', templateId);

    let headers: HttpHeaders = new HttpHeaders();
    headers = headers.append('Content-Type', 'application/json');
    headers = headers.append('responseType', 'json');
    return this.http.request('DELETE', endpoint,
      { headers, withCredentials: super.getWithCredentialsOption() });
  }
}
